import { useInfiniteQuery } from "@msuite/ui/query";
import { responseHandler, useWebSocket } from "@msuite/ui/utils";
import { guardArray } from "@msuite/utils/array";
import { mergeAttendanceSections } from "@msuite/utils/attendances";
import { type CalculatedTime, calculateTimeBySeconds, moment } from "@msuite/utils/date";
import { getPersonioLiveAttendanceState } from "@msuite/utils/integrations";
import { guardString } from "@msuite/utils/strings";
import { useEffect, useMemo, useOptimistic, useState } from "react";
import { client } from "@/hono";
import { useAuth } from "./use-auth";

const PAGE_SIZE = 5;

export function useAttendances(_userId?: string) {
	const auth = useAuth();
	const userId = _userId || auth.user?.id;
	const [calculatedTime, setCalculatedTime] = useState<CalculatedTime | null>(null);

	/** Query */
	const query = useInfiniteQuery({
		queryKey: ["attendances", guardString(_userId)],
		queryFn: async ({ pageParam }) => {
			if (!userId) throw new Error("User not authenticated");
			const response = await client.users[":userId"].attendances.$get({
				param: { userId },
				query: { offset: String(pageParam), pageSize: String(PAGE_SIZE) },
			});
			const data = await responseHandler(response);
			return data;
		},
		initialPageParam: 0,
		getNextPageParam: (lastPage, _allPages, lastPageParam) => {
			if (!lastPage.hasMore) return undefined;
			return lastPageParam + PAGE_SIZE;
		},
		staleTime: 1,
		enabled: !!auth.session?.data?.session?.id && !!userId,
		refetchOnMount: true,
		refetchOnWindowFocus: true,
	});

	const attendances = useMemo(
		() => mergeAttendanceSections(query.data?.pages.map((p) => p.sections) ?? []),
		[query.data?.pages],
	);

	useWebSocket({
		route: `/users/${userId}/attendances/updates`,
		onMessage: () => {
			query.refetch();
		},
	});

	const serverLiveState = getPersonioLiveAttendanceState(guardArray(attendances));

	const [liveState, setOptimisticLiveState] = useOptimistic(
		serverLiveState,
		(_, newState: typeof serverLiveState) => newState,
	);

	function handleSetCalculatedTime() {
		const differenceInSeconds = Math.abs(moment().diff(moment(liveState.liveSince), "seconds"));
		const time = calculateTimeBySeconds(differenceInSeconds);
		setCalculatedTime(time);
	}

	useEffect(() => {
		let interval: ReturnType<typeof setInterval> | null = null;
		if (liveState.isLive) {
			handleSetCalculatedTime();
			interval = setInterval(() => {
				handleSetCalculatedTime();
			}, 1000);
		} else {
			setCalculatedTime(null);
		}
		return () => {
			if (interval) {
				clearInterval(interval);
			}
		};
	}, [liveState.isLive]);

	return {
		...query,
		attendances,
		liveState,
		calculatedLiveTime: calculatedTime,
		setOptimisticLiveState,
	};
}
